import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { AutoAwesomeRounded, HelpOutlineRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { CapAssistedAnalysisProps, ReviewerDecision } from '@dosfilos/domain';
import { analysisIsStale } from '@dosfilos/domain';
import { formatDateTime } from '../../../lib/i18n-format';

// ─── El análisis asistido, al lado de la decisión ────────────────────────────
//
// NO ENCIMA DE LA DECISIÓN, al lado. La sugerencia se ve; los botones de decidir
// siguen siendo los mismos y siguen estando donde estaban. Poner el análisis
// arriba, o convertir su sugerencia en el botón destacado, haría que la decisión
// del revisor empezara siendo la del modelo — y la regla de esta épica es que la
// IA propone y la persona dispone.
//
// «USAR ESTE TEXTO» copia el borrador a la caja de observaciones. No envía nada:
// el revisor lo edita y después decide, con los mismos botones de siempre.

interface Props {
  analysis: CapAssistedAnalysisProps | null;
  currentVersion: number;
  busy: boolean;
  onGenerate: () => void;
  onUseDraft: (text: string) => void;
  /** Ronda ya decidida: el análisis se muestra como PARTE DEL EXPEDIENTE, sin
   *  poder generar otro ni copiarlo a ninguna parte. Analizar algo ya resuelto
   *  no tiene destinatario, y la caja de observaciones ya no existe. */
  readOnly?: boolean;
}

function decisionColor(d: ReviewerDecision): 'success' | 'warning' | 'error' | 'info' {
  if (d === 'approved') return 'success';
  if (d === 'approved_with_conditions') return 'info';
  if (d === 'returned') return 'warning';
  return 'error';
}

export function CapAnalysisPanel({
  analysis,
  currentVersion,
  busy,
  onGenerate,
  onUseDraft,
  readOnly = false,
}: Props) {
  const { t } = useTranslation('sis');

  const stale = analysis ? analysisIsStale(analysis, currentVersion) : false;

  // Una ronda cerrada que NUNCA tuvo análisis no muestra una caja vacía
  // ofreciendo algo que ya no se puede pedir.
  if (readOnly && !analysis) return null;

  return (
    // Sin fondo propio: este bloque vive DENTRO de la tarjeta de decisión, y
    // darle relleno lo convertía en una segunda tarjeta que competía con ella.
    // Un borde suave alcanza para decir «esto es auxiliar».
    <Box
      sx={{
        mt: 2.5,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* El botón va PEGADO al título, no empujado al borde derecho: en una
          tarjeta ancha, separarlos mil píxeles lo vuelve un enlace suelto que
          no se lee como la acción de este bloque. */}
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <AutoAwesomeRounded fontSize="small" color="action" />
          <Typography variant="subtitle2">{t('network.review.cap.analysis.title')}</Typography>
        </Stack>
        {!readOnly && (
          <Button
            size="small"
            variant="outlined"
            onClick={onGenerate}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : undefined}
          >
            {analysis
              ? t('network.review.cap.analysis.regenerate')
              : t('network.review.cap.analysis.generate')}
          </Button>
        )}
      </Stack>

      {/* UNA sola línea. Antes había dos que decían casi lo mismo: la ayuda
          («es una sugerencia») sólo hace falta cuando hay algo que sugerir, y
          el vacío ya explica por sí solo que no se ha pedido nada. */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {readOnly
          ? t('network.review.cap.analysis.archived')
          : analysis
            ? t('network.review.cap.analysis.help')
            : t('network.review.cap.analysis.empty')}
      </Typography>

      {analysis?.status === 'failed' && (
        // Se muestra el fracaso en vez de esconderlo: que el revisor sepa que se
        // intentó es distinto de que crea que nunca lo pidió.
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {t('network.review.cap.analysis.failed')}
          {analysis.error ? ` (${analysis.error})` : ''}
        </Alert>
      )}

      {analysis?.status === 'ready' && (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {stale && (
            // El centro presentó otra versión después de este análisis. Mostrar
            // la sugerencia sin decirlo sería describir un documento que ya no
            // es el que el revisor tiene delante.
            <Alert severity="warning">
              {t('network.review.cap.analysis.stale', { version: analysis.packetVersion })}
            </Alert>
          )}

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color={decisionColor(analysis.suggestedDecision!)}
              label={t(`network.review.cap.analysis.decision.${analysis.suggestedDecision}`)}
            />
            <Chip
              size="small"
              variant="outlined"
              label={t(`network.review.cap.analysis.confidence.${analysis.confidence}`)}
            />
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(analysis.createdAt)}
              {analysis.model ? ` · ${analysis.model}` : ''}
            </Typography>
          </Stack>

          {analysis.rationale && <Typography variant="body2">{analysis.rationale}</Typography>}

          {analysis.openQuestion && (
            // LO QUE EL SISTEMA NO SABE. El paquete no dice si un área faltante
            // se cubre en otro programa del centro, y esa es justamente la
            // diferencia entre devolver y aprobar con condiciones. Se pregunta
            // en vez de afirmar.
            <Alert severity="info" icon={<HelpOutlineRounded fontSize="inherit" />}>
              <Typography variant="caption" fontWeight={700} display="block">
                {t('network.review.cap.analysis.openQuestionTitle')}
              </Typography>
              {analysis.openQuestion}
            </Alert>
          )}

          {analysis.suggestedConditions.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={700} display="block">
                {t('network.review.cap.analysis.conditionsTitle')}
              </Typography>
              <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.5}>
                {analysis.suggestedConditions.map((c, i) => (
                  <Typography key={`${c.code}:${c.area ?? ''}:${i}`} component="li" variant="body2">
                    {c.note}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {analysis.draftObservations && (
            <Box>
              <Typography variant="caption" fontWeight={700} display="block">
                {t('network.review.cap.analysis.draftTitle')}
              </Typography>
              {/* El borrador se lee de verdad: es el texto que el revisor va a
                  editar. En gris claro se leía como una nota al pie. Va en una
                  caja con filete lateral, que dice «esto es una cita, todavía
                  no es tuyo» sin volverlo ilegible. */}
              <Box
                sx={{
                  mt: 0.75,
                  pl: 1.5,
                  borderLeft: '3px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {analysis.draftObservations}
                </Typography>
              </Box>
              {!readOnly && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1.25 }}
                  onClick={() => onUseDraft(analysis.draftObservations!)}
                >
                  {t('network.review.cap.analysis.useDraft')}
                </Button>
              )}
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}
